'use client'
import { memo, useCallback, type FC } from 'react'
import { useRouter } from 'next/navigation'
import styled from 'styled-components'
import { useSession, useTranslator } from '@/hooks'
import { LANG } from '@/utils'
import type { TypeCmsSlug, TypeCmsVariant } from '@/schema'
import { designGrid, mq, presets, vw } from '@/styles'
import { ProductSlider } from '../ProductSlider'
import { Button } from '../../base/Button'

type Props = {
  data: {
    category: {
      name: string
      slug: TypeCmsSlug
    }
    products: TypeCmsVariant[]
  }
  dataKey: string
  seeMoreButton?: boolean
  titleKey?: 'top_sellers' | 'highlighted'
}

const headingId = 'products-slider'
export const SectionProductSlider:FC<Props> = memo(({ data, dataKey, seeMoreButton, titleKey = 'top_sellers' }) => {
  const { category, products } = data || {}
  const { t } = useTranslator()
  const { user } = useSession()
  const { push } = useRouter()
  const sliderOptions = {
    cellAlign: 'left',
    autoPlay: false,
    prevNextButtons: false,
    imagesLoaded: true,
    draggable: true,
    wrapAround: false,
    ...(seeMoreButton ? {} : { contain: true }),
    ...(seeMoreButton ? {} : { groupCells: true })
  }

  const onClickSeeMore = useCallback(() => {
    push(`/${user ? 'shop' : 'catalog'}/${category?.slug[LANG]}`)
  }, [push, user, category?.slug])

  return (
    <Section aria-labelledby={headingId}>
      {seeMoreButton ? <div className='cta'>
        <h2 id={`${headingId}-${dataKey}`}>{category?.name}</h2>
        <Button className='see-more' onClick={onClickSeeMore} outline={true} bgColor={'--color-background'} color='--color-gold'>{t('project.see_all')}</Button>
      </div>
        :
        <div className='headline-wrapper'><h2>{t(`project.${titleKey}`)}</h2></div>
      }
      <ProductSlider data={products} className={seeMoreButton ? '' : 'slider'} sliderOptions={sliderOptions} seeMoreButton={seeMoreButton}/>
    </Section>
  )
})


const Section = styled.section`
  ${designGrid({})}
  margin: ${vw(40, 'mobile')} 0;
  overflow: hidden;
  width: 100vw;
  width: 100dvw;

  ${mq.greaterThan('tablet')} {
    margin: ${vw(40, 'desktop')} 0;
  }

  .cta {
    align-items: center;
    display: flex;
    flex-direction: row;
    grid-column: 1 / span 6;
    justify-content: space-between;
    padding: 0 0 ${vw(13, 'mobile')};
    min-height: ${vw(60, 'mobile')};

    ${mq.greaterThan('tablet')} {
      grid-column: 1 / span 12;
      padding: 0 0 ${vw(20, 'desktop')};
      min-height: ${vw(100, 'desktop')};
    }

    button {
      margin: 0;


      span {
        color: var(--color-text--yellow);
      }
    }

    h2 {
      ${presets.font45}
    }

  }

  .headline-wrapper {
    color: var(--color-text--darker);
    align-items: center;
    display: flex;
    grid-row: 1;
    grid-column: 1 / span 3;
    margin: 0 ${vw(20, 'mobile')};
		max-width: 400px;

    ${mq.greaterThan('nexus7')} {
      margin: 0 ${vw(20, 'tablet')};
    }

    ${mq.greaterThan('tablet')} {
      grid-area: 1 / 2 / span 1 / span 3;
      margin: 0;
    }
    > h2 {
      ${presets.font53_2}
      text-align: center;
    }

  }

  .slider {
    grid-row: 1;
    grid-column: 2 / span 4;

    ${mq.greaterThan('tablet')} {
      grid-column: 3 / span 8;
    }
  }
`
